import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";
import { Icon, IconSize } from "@gaming/xds-react-js";
import { CheckInCircle, ProfileSolid, Warning } from "@gaming/xds-react-js-icons";

import {
  getPartnerAccessDeniedPath,
  redirectToSignIn,
  xboxOnboardSiteLocationPrefix,
  XboxPublisherGuideUrl,
} from "../../constants";
import { FormattedText } from "../../components/FormattedText";
import { useScrollToTopOnLoad } from "../../helpers/windowHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { xboxOnboardActions } from "../../redux/actions/xboxOnboardActions";
import {
  getXboxOnboardData,
  getXboxOnboardIsLoading,
} from "../../redux/selectors/xboxOnboardSelectors";

import * as styles from "./PartnerSignupPage.module.css";

const PartnerSignupPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isSignedIn = useSelector(getIsUserSignedIn);
  const data = useSelector(getXboxOnboardData);
  const isLoading = useSelector(getXboxOnboardIsLoading);

  useScrollToTopOnLoad();

  const [
    title,
    subtitle,
    accountTitle,
    accountDescription,
    partnershipHeading,
    partnershipDescription,
    accountCheck1,
    accountCheck2,
    accountWarning,
    nextStepsHeading,
    nextStepsDescription,
    nextStepPartnerDetails,
    nextStepNda,
    nextStepGdk,
    nextStepPartnerCenter,
    nextStepIdXbox,
    nextStepIdXboxBenefits,
    cancelLabel,
    signInLabel,
    continueLabel,
    alreadySignedInMessage,
    sellerIdMessage,
  ] = useStrings(
    "partnerSignupTitle",
    "partnerSignupSubtitle",
    "partnerSignupAccountTitle",
    "partnerSignupAccountDescription",
    "partnerSignupPartnershipHeading",
    "partnerSignupPartnershipDescription",
    "partnerSignupAccountCheck1",
    "partnerSignupAccountCheck2",
    "partnerSignupAccountWarning",
    "partnerSignupNextStepsHeading",
    "partnerSignupNextStepsDescription",
    "partnerSignupNextStepPartnerDetails",
    "partnerSignupNextStepNda",
    "partnerSignupNextStepGdk",
    "partnerSignupNextStepPartnerCenter",
    "partnerSignupNextStepIdXbox",
    "partnerSignupNextStepIdXboxBenefits",
    "cancelButton",
    "partnerSignupSignInButton",
    "partnerSignupContinueButton",
    "partnerSignupAlreadySignedIn",
    "partnerSignupSellerIdMessage"
  );

  useEffect(() => {
    if (isSignedIn && !data && !isLoading) {
      dispatch(xboxOnboardActions.fetchData.start());
    }
  }, [data, dispatch, isLoading, isSignedIn]);

  useEffect(() => {
    if (data?.isPartnerCenterAccessDenied || data?.accessDeniedCode !== undefined) {
      history.replace(getPartnerAccessDeniedPath(data?.accessDeniedCode));
    }
  }, [data?.accessDeniedCode, data?.isPartnerCenterAccessDenied, history]);

  const handleCancel = () => {
    history.push(xboxOnboardSiteLocationPrefix);
  };

  const handleSignIn = () => {
    const onboardPath = `${xboxOnboardSiteLocationPrefix}/new`;
    if (isSignedIn) {
      history.push(onboardPath);
      return;
    }

    redirectToSignIn(onboardPath);
  };

  return (
    <>
      <div className={styles.pageContainer}>
        <div className={styles.content}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>

          <div className={styles.card}>
            <div className={styles.accountSection}>
              <div className={styles.accountIcon}>
                <Icon icon={ProfileSolid} iconSize={IconSize.Custom} />
              </div>
              <div>
                <h3 className={styles.accountTitle}>{accountTitle}</h3>
                <p className={styles.accountDescription}>
                  <FormattedText text={accountDescription} />
                </p>
              </div>
            </div>

            <h4 className={styles.sectionHeading}>{partnershipHeading}</h4>
            <p className={styles.sectionDescription}>{partnershipDescription}</p>
            {data?.sellerID && (
              <p className={styles.sellerIdNotice}>
                {sellerIdMessage.replace("{0}", data.sellerID)}
              </p>
            )}

            <ul className={styles.checkList}>
              <li className={styles.checkItem}>
                <Icon
                  className={styles.checkIcon}
                  icon={CheckInCircle}
                  iconSize={IconSize.Custom}
                />
                {accountCheck1}
              </li>
              <li className={styles.checkItem}>
                <Icon
                  className={styles.checkIcon}
                  icon={CheckInCircle}
                  iconSize={IconSize.Custom}
                />
                {accountCheck2}
              </li>
              <li className={styles.warningItem}>
                <Icon className={styles.warningIcon} icon={Warning} iconSize={IconSize.Custom} />
                <FormattedText text={accountWarning} />
              </li>
            </ul>

            <div className={styles.nextStepsSection}>
              <h4 className={styles.sectionHeading}>{nextStepsHeading}</h4>
              <p className={styles.sectionDescription}>{nextStepsDescription}</p>
              <ul className={styles.checkList}>
                <li className={styles.checkItem}>
                  <Icon
                    className={styles.checkIcon}
                    icon={CheckInCircle}
                    iconSize={IconSize.Custom}
                  />
                  <FormattedText text={nextStepPartnerDetails} />
                </li>
                <li className={styles.checkItem}>
                  <Icon
                    className={styles.checkIcon}
                    icon={CheckInCircle}
                    iconSize={IconSize.Custom}
                  />
                  <FormattedText text={nextStepNda} />
                </li>
                <li className={styles.checkItem}>
                  <Icon
                    className={styles.checkIcon}
                    icon={CheckInCircle}
                    iconSize={IconSize.Custom}
                  />
                  <FormattedText text={nextStepGdk} />
                </li>
                <li className={styles.checkItem}>
                  <Icon
                    className={styles.checkIcon}
                    icon={CheckInCircle}
                    iconSize={IconSize.Custom}
                  />
                  <FormattedText
                    text={nextStepPartnerCenter.replace("{0}", XboxPublisherGuideUrl)}
                  />
                </li>
                <li className={styles.checkItem}>
                  <Icon
                    className={styles.checkIcon}
                    icon={CheckInCircle}
                    iconSize={IconSize.Custom}
                  />
                  <FormattedText text={nextStepIdXbox} />
                </li>
                <li className={styles.checkItem}>
                  <Icon
                    className={styles.checkIcon}
                    icon={CheckInCircle}
                    iconSize={IconSize.Custom}
                  />
                  <FormattedText text={nextStepIdXboxBenefits} />
                </li>
              </ul>
            </div>

            <div className={styles.actions}>
              {isSignedIn && (
                <span className={styles.signedInMessage}>{alreadySignedInMessage}</span>
              )}
              <button className={styles.cancelButton} onClick={handleCancel}>
                {cancelLabel}
              </button>
              <button className={styles.signInButton} onClick={handleSignIn}>
                {isSignedIn ? continueLabel : signInLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PartnerSignupPage;
